import { FormEvent, useState } from 'react';
import { Mail, MapPin, Phone } from 'lucide-react';

const contactEmail = 'support@svklimited.co.uk';
const web3FormsEndpoint = 'https://api.web3forms.com/submit';
const web3FormsAccessKey = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY;
const workerAPIEndpoint = import.meta.env.VITE_LEADS_API_URL;
const isDevelopment = import.meta.env.DEV;

type ContactFormValues = {
  name: string;
  email: string;
  phone: string;
  companyName: string;
  message: string;
};

type ContactFormErrors = Partial<Record<keyof ContactFormValues, string>>;
type SubmitState = 'idle' | 'success' | 'error';

type Web3FormsPayload = {
  access_key: string;
  fullName: string;
  email: string;
  phone: string;
  companyName: string;
  subject: string;
  inquiryType: string;
  message: string;
  database_saved: 'yes' | 'no';
  database_success: 'true' | 'false';
  lead_id: string;
  database_error: string;
  websiteDomain: string;
};

type Web3FormsResponse = {
  success?: boolean;
  message?: string;
};

type LeadsApiPayload = {
  fullName: string;
  email: string;
  phone?: string;
  companyName?: string;
  subject?: string;
  inquiryType: string;
  message: string;
  consentMarketing: boolean;
  websiteDomain: string;
};

type LeadsApiResponse = {
  success: boolean;
  leadId?: string;
  error?: string;
};

const initialValues: ContactFormValues = {
  name: '',
  email: '',
  phone: '',
  companyName: '',
  message: '',
};

function validateContactForm(values: ContactFormValues) {
  const errors: ContactFormErrors = {};
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!values.name.trim()) {
    errors.name = 'Enter your name.';
  }

  if (!values.email.trim()) {
    errors.email = 'Enter your email address.';
  } else if (!emailPattern.test(values.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }

  if (!values.message.trim()) {
    errors.message = 'Describe the platform challenge.';
  }

  return errors;
}

function getTrimmedContactValues(values: ContactFormValues): ContactFormValues {
  return {
    name: values.name.trim(),
    email: values.email.trim(),
    phone: values.phone.trim(),
    companyName: values.companyName.trim(),
    message: values.message.trim(),
  };
}

function getLeadPayload(values: ContactFormValues): LeadsApiPayload {
  const trimmedValues = getTrimmedContactValues(values);

  return {
    fullName: trimmedValues.name,
    email: trimmedValues.email,
    phone: trimmedValues.phone,
    companyName: trimmedValues.companyName,
    message: trimmedValues.message,
    subject: `SVK Limited platform enquiry from ${trimmedValues.name}`,
    inquiryType: 'platform_inquiry',
    consentMarketing: false,
    websiteDomain: window.location.hostname,
  };
}

function getWeb3FormsPayload(
  formData: LeadsApiPayload,
  accessKey: string,
  workerResult: LeadsApiResponse,
): Web3FormsPayload {
  return {
    access_key: accessKey,
    fullName: formData.fullName,
    email: formData.email,
    phone: formData.phone ?? '',
    companyName: formData.companyName ?? '',
    subject: formData.subject ?? '',
    inquiryType: 'platform_inquiry',
    message: formData.message,
    database_saved: workerResult.success ? 'yes' : 'no',
    database_success: workerResult.success ? 'true' : 'false',
    lead_id: workerResult.leadId || 'not_available',
    database_error: workerResult.error || '',
    websiteDomain: formData.websiteDomain,
  };
}

async function submitLeadToWorker(payload: LeadsApiPayload): Promise<LeadsApiResponse> {
  if (!workerAPIEndpoint) {
    console.error('VITE_LEADS_API_URL is not configured');

    return {
      success: false,
      error: 'Worker API endpoint is not configured',
    };
  }

  if (isDevelopment) {
    console.log('Submitting lead to Worker API', {
      endpoint: workerAPIEndpoint,
      payload,
    });
  }

  try {
    const response = await fetch(workerAPIEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const result = (await response.json()) as LeadsApiResponse;

    if (isDevelopment) {
      console.log('Worker API response', result);
    }

    return result;
  } catch (error) {
    console.error('Worker API failed', error);

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown Worker API error',
    };
  }
}

export function Contact() {
  const [values, setValues] = useState<ContactFormValues>(initialValues);
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const nextErrors = validateContactForm(values);
    setErrors(nextErrors);
    setSubmitState('idle');
    setStatusMessage('');

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setIsSubmitting(true);

    const leadPayload = getLeadPayload(values);
    const workerResult = await submitLeadToWorker(leadPayload);
    const databaseSaved = workerResult.success;

    if (!databaseSaved) {
      console.error('Worker API failed', workerResult.error);
    }

    if (!web3FormsAccessKey) {
      const missingKeyError = new Error('Missing VITE_WEB3FORMS_ACCESS_KEY environment variable.');
      console.error(missingKeyError);
      setSubmitState('error');
      setStatusMessage('The enquiry form is not configured yet. Please email support@svklimited.co.uk directly.');
      setIsSubmitting(false);
      return;
    }

    const payload = getWeb3FormsPayload(leadPayload, web3FormsAccessKey, workerResult);

    try {
      const response = await fetch(web3FormsEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = (await response.json()) as Web3FormsResponse;

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Web3Forms rejected the enquiry.');
      }

      setValues(initialValues);
      setErrors({});
      setSubmitState('success');
      setStatusMessage('Thanks. Your enquiry has been sent and SVK Limited will respond soon.');

      if (!databaseSaved) {
        console.warn('Enquiry email was sent, but the lead was not saved to the database.');
      }
    } catch (error) {
      console.error('Web3Forms submission failed:', error);
      if (databaseSaved) {
        setValues(initialValues);
        setErrors({});
        setSubmitState('success');
        setStatusMessage('Thanks. Your enquiry has been sent and SVK Limited will respond soon.');
      } else {
        setSubmitState('error');
        setStatusMessage('Sorry, the enquiry could not be sent. Please email support@svklimited.co.uk directly.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="mx-auto grid max-w-7xl gap-10 px-5 py-16 lg:grid-cols-[0.9fr_1.1fr] lg:px-8">
      <div>
        <p className="text-sm font-bold uppercase tracking-[0.18em] text-copper">Contact</p>
        <h1 className="mt-3 text-4xl font-bold md:text-6xl">Discuss your platform modernisation goals.</h1>
        <p className="mt-5 text-lg leading-8 text-slate-600">
          Share the platform, API, cloud, identity, or modernisation challenge you are working through. SVK Limited can
          help clarify the architecture, risks, delivery route, and next commercial decision.
        </p>

        <div className="mt-8 grid gap-3">
          {[
            'Book a Consultation',
            'Discuss Your Platform',
            'API Modernisation Review',
            'Cloud Architecture Assessment',
          ].map((item) => (
            <p key={item} className="border-l-4 border-cyan bg-white px-4 py-3 text-sm font-bold shadow-soft">
              {item}
            </p>
          ))}
        </div>

        <div className="mt-10 grid gap-4 text-sm text-slate-700">
          <p className="flex items-center gap-3">
            <Mail size={20} className="text-forest" /> {contactEmail}
          </p>
          <p className="flex items-center gap-3">
            <Phone size={20} className="text-forest" /> Available by appointment
          </p>
          <p className="flex items-center gap-3">
            <MapPin size={20} className="text-forest" /> United Kingdom
          </p>
        </div>
      </div>

      <form className="rounded border border-slate-200 bg-white p-6 shadow-soft" onSubmit={handleSubmit} noValidate>
        <label className="form-label" htmlFor="name">
          Name
        </label>
        <input
          className="form-input"
          id="name"
          name="name"
          type="text"
          autoComplete="name"
          value={values.name}
          onChange={(event) => setValues((current) => ({ ...current, name: event.target.value }))}
          aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? 'name-error' : undefined}
        />
        {errors.name && (
          <p className="mt-2 text-sm font-semibold text-red-600" id="name-error">
            {errors.name}
          </p>
        )}

        <label className="form-label" htmlFor="email">
          Email
        </label>
        <input
          className="form-input"
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          value={values.email}
          onChange={(event) => setValues((current) => ({ ...current, email: event.target.value }))}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? 'email-error' : undefined}
        />
        {errors.email && (
          <p className="mt-2 text-sm font-semibold text-red-600" id="email-error">
            {errors.email}
          </p>
        )}

        <label className="form-label" htmlFor="phone">
          Phone
        </label>
        <input
          className="form-input"
          id="phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          value={values.phone}
          onChange={(event) => setValues((current) => ({ ...current, phone: event.target.value }))}
        />

        <label className="form-label" htmlFor="companyName">
          Company Name
        </label>
        <input
          className="form-input"
          id="companyName"
          name="companyName"
          type="text"
          autoComplete="organization"
          value={values.companyName}
          onChange={(event) => setValues((current) => ({ ...current, companyName: event.target.value }))}
        />

        <label className="form-label" htmlFor="message">
          Platform challenge
        </label>
        <textarea
          className="form-input min-h-40 resize-y"
          id="message"
          name="message"
          value={values.message}
          onChange={(event) => setValues((current) => ({ ...current, message: event.target.value }))}
          aria-invalid={Boolean(errors.message)}
          aria-describedby={errors.message ? 'message-error' : undefined}
        />
        {errors.message && (
          <p className="mt-2 text-sm font-semibold text-red-600" id="message-error">
            {errors.message}
          </p>
        )}

        <button className="btn-primary mt-6 w-full justify-center disabled:cursor-not-allowed disabled:opacity-70" type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Sending enquiry...' : 'Send platform enquiry'}
        </button>
        {statusMessage && (
          <p
            className={`mt-4 border-l-4 px-4 py-3 text-sm font-semibold ${
              submitState === 'success'
                ? 'border-forest bg-green-50 text-forest'
                : submitState === 'error'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-cyan bg-cyan/10 text-slate-700'
            }`}
            role={submitState === 'error' ? 'alert' : 'status'}
          >
            {statusMessage}
          </p>
        )}
      </form>
    </section>
  );
}
